/**
 * Mock data for No-Show Predictor UI
 * 
 * TODO: Remove this file after backend API implementation is complete.
 * Uses existing therapists from appointments.mock.ts for consistency.
 */

import {
  IPatientRisk,
  INoShowStats,
  IOverbookingSuggestion,
  IDayCancellationRate,
  IRiskCounts
} from '../types/noShowPredictor.types';
import { ITherapist } from '../types/appointments.types';

// Mock therapists for no-show predictor
const mockTherapists: ITherapist[] = [
  { id: 1, name: 'ד"ר יעל כהן', isActive: true },
  { id: 2, name: 'ד"ר דוד לוי', isActive: true },
  { id: 3, name: 'ד"ר שרה אברהם', isActive: true },
];

// Get therapist name helper
const getTherapistName = (id: number): string => {
  return mockTherapists.find((t: ITherapist) => t.id === id)?.name || 'Unknown';
};

// Mock Statistics
export const mockNoShowStats: INoShowStats = {
  highRiskCount: 12,
  predictionAccuracy: 87,
  averageCancellationRate: 8.3,
  preventedThisWeek: 23,
  highRiskTrend: 3, // +3 from yesterday
  accuracyTrend: 2, // +2% from last month
  cancellationTrend: -1.2 // -1.2% (improvement!)
};

// Mock Risk Counts
export const mockRiskCounts: IRiskCounts = {
  high: 12,
  medium: 8,
  low: 43
};

// Mock Overbooking Suggestions
export const mockOverbookingSuggestions: IOverbookingSuggestion[] = [
  {
    id: 1,
    date: '2025-12-25',
    dayName: 'רביעי',
    timeSlot: '14:00-15:00',
    therapistId: 1,
    therapistName: getTherapistName(1),
    confidenceScore: 92,
    reason: 'בזמן הזה יש היסטוריה של 35% ביטולים',
    scheduledAppointments: 3,
    suggestedAdditions: 1
  },
  {
    id: 2,
    date: '2025-12-26',
    dayName: 'חמישי',
    timeSlot: '16:00-17:00',
    therapistId: 2,
    therapistName: getTherapistName(2),
    confidenceScore: 88,
    reason: 'יום חמישי אחה"צ: 28% ביטולים',
    scheduledAppointments: 2,
    suggestedAdditions: 1
  },
  {
    id: 3,
    date: '2025-12-22',
    dayName: 'ראשון',
    timeSlot: '08:00-09:00',
    therapistId: 1,
    therapistName: getTherapistName(1),
    confidenceScore: 85,
    reason: 'ראשון בבוקר מוקדם: 31% ביטולים (שינה יתר)',
    scheduledAppointments: 1,
    suggestedAdditions: 1
  }
];

// Mock Cancellation Rates by Day
export const mockDayCancellationRates: IDayCancellationRate[] = [
  { dayKey: 'SUNDAY', dayName: 'ראשון', rate: 12 },
  { dayKey: 'MONDAY', dayName: 'שני', rate: 7 },
  { dayKey: 'TUESDAY', dayName: 'שלישי', rate: 5 },
  { dayKey: 'WEDNESDAY', dayName: 'רביעי', rate: 8 },
  { dayKey: 'THURSDAY', dayName: 'חמישי', rate: 10 }
];

// Mock Patients at Risk
export const mockPatientsAtRisk: IPatientRisk[] = [
  // High Risk Patients
  {
    id: 1,
    patientName: 'דניאל כהן',
    appointmentDate: '2025-12-25',
    appointmentTime: '14:00',
    therapistId: 1,
    therapistName: getTherapistName(1),
    riskLevel: 'high',
    riskPercentage: 78,
    riskFactors: [
      { id: 'f1', text: 'ביטל 3 מתוך 5 תורים אחרונים', isNegative: true },
      { id: 'f2', text: 'לא ענה לתזכורת SMS', isNegative: true },
      { id: 'f3', text: 'יום רביעי (יום בעייתי עבורו)', isNegative: true }
    ],
    predictionDetails: {
      historicalPattern: '60% ביטולים ביום רביעי',
      reminderResponse: '48 שעות (לא נפתח)',
      previousAppointments: '3 ביטולים, 2 הגיעו'
    },
    reminderSent: true,
    reminderConfirmed: false
  },
  {
    id: 2,
    patientName: 'מיכל לוי',
    appointmentDate: '2025-12-26',
    appointmentTime: '16:00',
    therapistId: 2,
    therapistName: getTherapistName(2),
    riskLevel: 'high',
    riskPercentage: 72,
    riskFactors: [
      { id: 'f1', text: 'תור אחרון בוטל ברגע האחרון', isNegative: true },
      { id: 'f2', text: 'חמישי אחה"צ (זמן בעייתי)', isNegative: true },
      { id: 'f3', text: 'אישרה הגעה ב-SMS', isNegative: false }
    ],
    predictionDetails: {
      historicalPattern: '50% ביטולים בימי חמישי',
      reminderResponse: 'אישרה לפני 24 שעות',
      previousAppointments: '2 ביטולים, 3 הגיעו'
    },
    reminderSent: true,
    reminderConfirmed: true
  },
  {
    id: 3,
    patientName: 'יעקב ברוך',
    appointmentDate: '2025-12-24',
    appointmentTime: '11:00',
    therapistId: 3,
    therapistName: getTherapistName(3),
    riskLevel: 'high',
    riskPercentage: 68,
    riskFactors: [
      { id: 'f1', text: 'לא הגיע לתור קודם ללא הודעה', isNegative: true },
      { id: 'f2', text: 'מטופל חדש - אין היסטוריה', isNegative: true }
    ],
    predictionDetails: {
      historicalPattern: 'מטופל חדש',
      reminderResponse: 'לא נפתח',
      previousAppointments: '1 אי-הגעה'
    },
    reminderSent: true,
    reminderConfirmed: false
  },
  // Medium Risk Patients
  {
    id: 4,
    patientName: 'אבי מזרחי',
    appointmentDate: '2025-12-24',
    appointmentTime: '10:00',
    therapistId: 4,
    therapistName: getTherapistName(4),
    riskLevel: 'medium',
    riskPercentage: 45,
    riskFactors: [
      { id: 'f1', text: 'לא פתח תזכורת מייל', isNegative: true },
      { id: 'f2', text: 'הגיע ל-4 תורים אחרונים', isNegative: false },
      { id: 'f3', text: 'זמן טוב (בוקר)', isNegative: false }
    ],
    predictionDetails: {
      historicalPattern: '20% ביטולים בבוקר',
      reminderResponse: 'לא פתח מייל (SMS בדרך)',
      previousAppointments: '1 ביטול, 9 הגיעו'
    },
    reminderSent: true,
    reminderConfirmed: false
  },
  {
    id: 5,
    patientName: 'רונית שמעון',
    appointmentDate: '2025-12-25',
    appointmentTime: '09:00',
    therapistId: 5,
    therapistName: getTherapistName(5),
    riskLevel: 'medium',
    riskPercentage: 38,
    riskFactors: [
      { id: 'f1', text: 'ביטלה פעם אחת בחודש האחרון', isNegative: true },
      { id: 'f2', text: 'מגיעה בדרך כלל בזמן', isNegative: false }
    ],
    predictionDetails: {
      historicalPattern: '15% ביטולים',
      reminderResponse: 'אישרה תוך שעה',
      previousAppointments: '2 ביטולים, 10 הגיעו'
    },
    reminderSent: true,
    reminderConfirmed: true
  },
  // Low Risk Patients
  {
    id: 6,
    patientName: 'שרה ברק',
    appointmentDate: '2025-12-23',
    appointmentTime: '09:00',
    therapistId: 1,
    therapistName: getTherapistName(1),
    riskLevel: 'low',
    riskPercentage: 12,
    riskFactors: [
      { id: 'f1', text: 'הגיעה לכל 12 התורים', isNegative: false },
      { id: 'f2', text: 'אישרה הגעה מיד', isNegative: false },
      { id: 'f3', text: 'זמן מועדף (בוקר מוקדם)', isNegative: false }
    ],
    predictionDetails: {
      historicalPattern: '0% ביטולים (מעולה!)',
      reminderResponse: 'אישרה תוך 2 שעות',
      previousAppointments: '0 ביטולים, 12 הגיעו'
    },
    reminderSent: true,
    reminderConfirmed: true
  },
  {
    id: 7,
    patientName: 'משה דוד',
    appointmentDate: '2025-12-23',
    appointmentTime: '14:00',
    therapistId: 6,
    therapistName: getTherapistName(6),
    riskLevel: 'low',
    riskPercentage: 8,
    riskFactors: [
      { id: 'f1', text: 'מטופל קבוע - 2 שנים', isNegative: false },
      { id: 'f2', text: 'אף פעם לא ביטל', isNegative: false }
    ],
    predictionDetails: {
      historicalPattern: '0% ביטולים',
      reminderResponse: 'אישר אוטומטית',
      previousAppointments: '0 ביטולים, 24 הגיעו'
    },
    reminderSent: true,
    reminderConfirmed: true
  },
  {
    id: 8,
    patientName: 'יפה גולן',
    appointmentDate: '2025-12-24',
    appointmentTime: '15:00',
    therapistId: 7,
    therapistName: getTherapistName(7),
    riskLevel: 'low',
    riskPercentage: 15,
    riskFactors: [
      { id: 'f1', text: 'ביטלה פעם אחת בשנה', isNegative: true },
      { id: 'f2', text: 'מגיעה באופן קבוע', isNegative: false },
      { id: 'f3', text: 'אישרה הגעה', isNegative: false }
    ],
    predictionDetails: {
      historicalPattern: '5% ביטולים',
      reminderResponse: 'אישרה',
      previousAppointments: '1 ביטול, 18 הגיעו'
    },
    reminderSent: true,
    reminderConfirmed: true
  }
];
